import type {
  MachineAttachmentItemDto,
  MachineQuotationItemDto,
  QuotationDto,
  QuotationPriceOnlyDto
} from '@api/machines'
import { EQUIPMENT_TYPE, QuoteItemStockStatus } from '@common/utils'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { BannerType } from '../banner/Banner'
import { useQuoteEquipmentStatus } from './useQuoteEquipmentStatus'

export const useQuoteEquipmentUtils = (
  machine: MachineQuotationItemDto & MachineAttachmentItemDto,
  quotation: QuotationDto | undefined,
  quoteNewPrice: QuotationPriceOnlyDto,
  isNestedAttachment?: boolean
) => {
  const theme = molsonUseTheme()
  const { t } = useTranslation('')
  const commonTranslationKey = 'common'
  const nonCommonTranslationKey = 'newQuote.itemsTab'

  const {
    itemIsOutOfStock: isEqOutOfStock,
    isEqArchived,
    isEqBrandArchived,
    itemUsesLifeCycleCosting,
    itemLifeCycleCostChange,
    itemUsesNetCapitalizedCosting,
    itemsPriceUpdatesCount,
    problematicItemsCount
  } = useQuoteEquipmentStatus(
    machine,
    quotation,
    quoteNewPrice,
    isNestedAttachment
  )

  const isSpecEquipment = Boolean(machine.specInfo)
  const isEquipmentIncomplete = machine.incomplete
  const isEquipmentSold =
    machine.stockInfo?.stockStatus === QuoteItemStockStatus.SOLD
  const isEquipmentSoldPendingDelivery =
    machine.stockInfo?.stockStatus ===
    QuoteItemStockStatus.SOLD_PENDING_DELIVERY
  const requirePriceUpdate = machine.priceChanged

  const equipmentLccPriceChanged =
    itemLifeCycleCostChange.changed &&
    itemUsesLifeCycleCosting &&
    itemsPriceUpdatesCount === 0

  const equipmentLccChangedAndPriceUpdates =
    itemLifeCycleCostChange.changed &&
    itemUsesLifeCycleCosting &&
    itemsPriceUpdatesCount > 0

  const equipmentWithLccAndNoPriceUpdates =
    itemUsesLifeCycleCosting && itemsPriceUpdatesCount === 0
  const equipmentWithLccAndPriceUpdates =
    itemUsesLifeCycleCosting && itemsPriceUpdatesCount > 0
  const equipmentWithNccAndNoPriceUpdates =
    itemUsesNetCapitalizedCosting && itemsPriceUpdatesCount === 0
  const equipmentWithNccAndPriceUpdates =
    itemUsesNetCapitalizedCosting && itemsPriceUpdatesCount > 0

  // biome-ignore lint/style/useNamingConvention: <->
  const hasLCCorNCCpriceUpdate =
    itemUsesLifeCycleCosting ||
    itemLifeCycleCostChange.changed ||
    itemUsesNetCapitalizedCosting

  const LccPriceChangeType = itemLifeCycleCostChange.increased
    ? t(`${commonTranslationKey}.increased`)
    : t(`${commonTranslationKey}.decreased`)

  const getBannerText = () => {
    let bannerText = ''
    switch (true) {
      case isEquipmentSold:
        bannerText = t(
          `${nonCommonTranslationKey}.cards.machineCard.itemSoldOut`
        )
        break

      case equipmentLccPriceChanged:
        bannerText = t(`${commonTranslationKey}.lccForMachineUpdated`, {
          changeType: LccPriceChangeType
        })
        break

      case equipmentLccChangedAndPriceUpdates:
        bannerText = t(
          `${commonTranslationKey}.lccForMachineUpdatedAndChanges`,
          {
            changeType: LccPriceChangeType,
            nrOfItems: itemsPriceUpdatesCount
          }
        )
        break

      case equipmentWithLccAndNoPriceUpdates:
        bannerText = t(`${commonTranslationKey}.pricingForMachineChangedToLcc`)
        break

      case equipmentWithLccAndPriceUpdates:
        bannerText = t(
          `${commonTranslationKey}.pricingForMachineChangedToLccAndCostChanges`,
          {
            nrOfItems: itemsPriceUpdatesCount
          }
        )
        break

      case equipmentWithNccAndNoPriceUpdates:
        bannerText = t(`${commonTranslationKey}.pricingForMachineChangedToNcc`)
        break

      case equipmentWithNccAndPriceUpdates:
        bannerText = t(
          `${commonTranslationKey}.pricingForMachineChangedToNccAndCostChanges`,
          {
            nrOfItems: itemsPriceUpdatesCount
          }
        )
        break

      default:
        break
    }
    return bannerText
  }

  const handleBannerData = () => {
    // Equipment or its brand is archived -> ERROR (red card border)
    if (isEqArchived || isEqBrandArchived) {
      return {
        text: isEqArchived
          ? t(`${commonTranslationKey}.equipmentWasArchived`, {
              equipmentType:
                machine.machineType?.toLowerCase() ?? EQUIPMENT_TYPE.EQUIPMENT
            })
          : t(`${commonTranslationKey}.itemBrandWasArchived`),
        btnText: t(`${commonTranslationKey}.remove`),
        cardBorder: `2px solid ${theme.palette.primary.error}`,
        bannerType: BannerType.Error
      }
    }

    // Equipment has incomplete items and/or price updates -> ERROR (red card border)
    if (
      isEquipmentIncomplete ||
      (isEquipmentIncomplete && requirePriceUpdate)
    ) {
      return {
        text: t(`${commonTranslationKey}.itemsInMachineHaveErrors`, {
          nrOfItems: problematicItemsCount
        }),
        btnText: t(`${commonTranslationKey}.reviseEquipmentType`, {
          equipmentType: machine.machineType
        }),
        cardBorder: `2px solid ${theme.palette.primary.error}`,
        bannerType: BannerType.Error
      }
    }

    // Equipment only requires price updates but unrelated to LCC/NCC -> WARNING (orange card border)
    if (
      !isEquipmentIncomplete &&
      requirePriceUpdate &&
      !hasLCCorNCCpriceUpdate &&
      !isEqOutOfStock
    ) {
      return {
        text: t(`${commonTranslationKey}.itemsInMachineHaveCostChanges`, {
          nrOfItems: itemsPriceUpdatesCount
        }),
        btnText: t(`${commonTranslationKey}.checkUpdates`, {
          equipmentType: machine.machineType
        }),
        cardBorder: `2px solid ${theme.palette.secondary[300]}`,
        bannerType: BannerType.Warning
      }
    }

    // Is Spec equipment and has only price updates -> WARNING (orange card border)
    if (isSpecEquipment && requirePriceUpdate) {
      return {
        text: t(`${commonTranslationKey}.itemsInMachineHaveCostChanges`, {
          nrOfItems: itemsPriceUpdatesCount
        }),
        btnText: t(`${commonTranslationKey}.reviseEquipmentType`, {
          equipmentType: machine.machineType
        }),
        cardBorder: `2px solid ${theme.palette.primary.light}`,
        bannerType: BannerType.Warning
      }
    }

    // Is Spec equipment and has no issues -> NULL (green card border)
    if (isSpecEquipment && !requirePriceUpdate && !isEquipmentIncomplete) {
      return {
        text: '',
        btnText: '',
        cardBorder: `2px solid ${theme.palette.primary.light}`,
        bannerType: null
      }
    }

    if (hasLCCorNCCpriceUpdate && !isEqOutOfStock) {
      return {
        text: getBannerText(),
        btnText: t(`${commonTranslationKey}.checkUpdates`, {
          equipmentType: machine.machineType
        }),
        cardBorder: null,
        bannerType: BannerType.Warning
      }
    }

    if (isEquipmentSold) {
      return {
        text: getBannerText(),
        btnText: '',
        cardBorder: `2px solid ${theme.palette.primary.error}`,
        bannerType: BannerType.Error
      }
    }

    if (isEquipmentSoldPendingDelivery) {
      const soldPendingDeliveryBannerType = null
      const soldPendingDeliveryCardBorder = hasLCCorNCCpriceUpdate
        ? `2px solid ${theme.palette.primary.warningBorderSecondary}`
        : null

      return {
        text: getBannerText(),
        btnText: t(`${commonTranslationKey}.checkUpdates`, {
          equipmentType: machine.machineType
        }),
        cardBorder: soldPendingDeliveryCardBorder,
        bannerType: soldPendingDeliveryBannerType
      }
    }

    return {
      text: '',
      btnText: '',
      cardBorder: null,
      bannerType: null
    }
  }

  const {
    text: bannerText,
    btnText: bannerButtonText,
    cardBorder,
    bannerType
  } = handleBannerData()

  return {
    bannerType,
    cardBorder,
    bannerText,
    bannerButtonText
  }
}
